import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Crown, Apple, RefreshCw } from "lucide-react";
import { mobileFlags } from "../lib/mobileFeatureFlags";
import { purchasePractitionerSubscription, restorePurchases } from "../lib/appleIAP";
import { purchasePractitionerSubscriptionAndroid, restorePurchasesAndroid } from "../lib/googlePlayBilling";

export function SubscribePage() {
  const { user, profile, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [elderSlots, setElderSlots] = useState(null);
  const nav = useNavigate();

  // Pull live elder-slot count for the tease banner
  useEffect(() => {
    api.get("/rewards/public-stats").then(({ data }) => setElderSlots(data?.elderSlotsRemaining))
      .catch(() => { /* anonymous fallback */ });
  }, []);

  const subscribeStripe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/init", {
        origin: window.location.origin,
        kind: "subscription",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setLoading(false);
    }
  };

  const subscribeApple = async () => {
    setLoading(true);
    try {
      const res = await purchasePractitionerSubscription();
      if (!res.ok) {
        toast.error(`Apple Pay couldn't start (${res.reason}). Try again.`);
        setLoading(false);
        return;
      }
      // The cordova-plugin-purchase `approved` listener posts the receipt
      // to our backend asynchronously; poll auth context until activation
      // lands so the UI updates without a manual refresh.
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        await refresh();
        const ok = (window.__lastProfile?.subscriptionStatus === "active");
        if (ok || tries >= 10) {
          clearInterval(poll);
          setLoading(false);
          if (ok) toast.success("Subscription activated via Apple");
        }
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Apple IAP failed");
      setLoading(false);
    }
  };

  const subscribeGoogle = async () => {
    setLoading(true);
    try {
      const res = await purchasePractitionerSubscriptionAndroid();
      if (!res.ok) {
        toast.error(`Google Play couldn't start (${res.reason}). Try again.`);
        setLoading(false);
        return;
      }
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        await refresh();
        const ok = (window.__lastProfile?.subscriptionStatus === "active");
        if (ok || tries >= 10) {
          clearInterval(poll);
          setLoading(false);
          if (ok) toast.success("Subscription activated via Google Play");
        }
      }, 1500);
    } catch (e) {
      toast.error(e.message || "Google Play billing failed");
      setLoading(false);
    }
  };

  const restoreApple = async () => {
    setRestoring(true);
    const res = mobileFlags.useGooglePlayBilling
      ? await restorePurchasesAndroid()
      : await restorePurchases();
    if (res.ok) {
      setTimeout(async () => { await refresh(); toast.success("Restored"); setRestoring(false); }, 2000);
    } else {
      toast.error(`Restore failed: ${res.reason}`);
      setRestoring(false);
    }
  };

  const subscribe = mobileFlags.useAppleIAP
    ? subscribeApple
    : mobileFlags.useGooglePlayBilling
      ? subscribeGoogle
      : subscribeStripe;

  useEffect(() => {
    if (!user) return;
    // Admins have an always-active house subscription — they don't need to pay
    if (user?.role === "ADMIN") nav("/profile/" + user.id);
    else if (user?.role !== "PROVIDER") nav("/feed");
  }, [user, nav]);

  return (
    <Layout>
      <div className="parchment p-12 text-center">
        <div className="label-mystic mb-4">Practitioner Membership</div>
        <h1 className="font-serif text-6xl mb-6">Join the Coven</h1>
        <div className="font-serif text-7xl text-secondary mb-2">$49<span className="text-3xl">.99</span></div>
        <div className="label-mystic mb-10">per moon-cycle</div>
        {elderSlots != null && elderSlots > 0 && (
          <div className="max-w-md mx-auto mb-8 parchment p-4 border-primary/40 bg-primary/[0.04]" data-testid="elder-tease">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Crown size={16} className="text-primary" strokeWidth={2} />
              <span className="label-mystic text-primary">Early Practitioner Reward</span>
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed">
              You&apos;ll be one of the first <b>500</b> practitioners — claim the permanent <b>Elder</b> badge that appears on your profile, posts and clips forever.
              <span className="block text-secondary mt-1">{elderSlots.toLocaleString()} Elder slots remaining</span>
            </p>
          </div>
        )}
        <ul className="text-left max-w-md mx-auto space-y-4 mb-10">
          {[
            "Publish unlimited services & rituals",
            "Accept payments directly to your bank",
            "Livestream ceremonies & paid rooms",
            "Voice & video consultations",
            "Receive verified reviews",
            "Run paid ads with Cauldron Reward Token",
          ].map((b) => (
            <li key={b} className="flex gap-3 text-foreground/85">
              <span className="text-primary mt-1">✦</span>
              {b}
            </li>
          ))}
        </ul>
        {profile?.subscriptionStatus === "active" ? (
          <div className="space-y-4">
            <div className="text-secondary font-serif text-2xl" data-testid="subscription-active">Active — your shrine is open.</div>
            {profile.subscriptionExpiresAt && (
              <div className="label-mystic">Renews: {new Date(profile.subscriptionExpiresAt).toLocaleDateString()}</div>
            )}
            <button onClick={async () => {
                if (!window.confirm("Cancel subscription? Your shrine will close at end of period.")) return;
                try { await api.post("/subscription/cancel"); await refresh(); toast.success("Subscription cancelled"); }
                catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              className="border border-destructive/60 text-destructive px-6 py-2 rounded-sm uppercase tracking-wider text-xs"
              data-testid="subscription-cancel-btn">
              Cancel Subscription
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <button onClick={subscribe} disabled={loading}
              className="bg-primary text-primary-foreground px-12 py-4 uppercase tracking-[0.2em] text-sm rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="subscribe-checkout-btn">
              {mobileFlags.useAppleIAP && <Apple size={16} />}
              {loading
                ? (mobileFlags.useAppleIAP
                    ? "Opening Apple Pay..."
                    : mobileFlags.useGooglePlayBilling
                      ? "Opening Google Play..."
                      : "Opening Stripe...")
                : (mobileFlags.useAppleIAP
                    ? "Subscribe with Apple · $49.99/mo"
                    : mobileFlags.useGooglePlayBilling
                      ? "Subscribe with Google Play · $49.99/mo"
                      : "Subscribe Now · Recurring Monthly")}
            </button>
            {(mobileFlags.useAppleIAP || mobileFlags.useGooglePlayBilling) && (
              <button
                onClick={restoreApple}
                disabled={restoring}
                className="text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-secondary inline-flex items-center gap-1.5 px-3 py-1.5"
                data-testid="subscribe-restore-btn"
              >
                <RefreshCw size={11} className={restoring ? "animate-spin" : ""} />
                Restore Purchases
              </button>
            )}
          </div>
        )}
        <div className="text-xs text-foreground/50 mt-6">
          {mobileFlags.useAppleIAP
            ? "Recurring monthly · Billed by Apple via your iCloud account · Cancel anytime in Settings"
            : mobileFlags.useGooglePlayBilling
              ? "Recurring monthly · Billed by Google Play · Cancel anytime in the Play Store"
              : "Recurring monthly subscription · Secure payment by Stripe · Test mode · Cancel anytime"}
        </div>
      </div>

      {profile?.subscriptionStatus === "active" && (
        <div className="parchment p-10 text-center mt-8" data-testid="verified-badge-section">
          <div className="label-mystic mb-3">Add-On</div>
          <h2 className="font-serif text-4xl mb-3">Verified Practitioner Badge</h2>
          <div className="font-serif text-5xl text-secondary mb-2">$9<span className="text-2xl">.99</span></div>
          <div className="label-mystic mb-6">per moon-cycle</div>
          <p className="text-foreground/70 max-w-lg mx-auto mb-6 text-sm leading-relaxed">
            A gold check beside your name across the feed, profile, and marketplace.
            Boosts trust, conversion, and feed ranking.
          </p>
          {profile?.verified ? (
            <div className="space-y-3">
              <div className="text-secondary font-serif text-xl" data-testid="verified-active">✦ Verified · Trusted by Cauldron</div>
              <button
                onClick={async () => {
                  if (!window.confirm("Cancel verified badge?")) return;
                  try { await api.post("/verified/cancel"); await refresh(); toast.success("Badge cancelled"); }
                  catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                }}
                className="border border-destructive/60 text-destructive px-5 py-2 rounded-sm uppercase tracking-wider text-xs"
                data-testid="verified-cancel-btn"
              >
                Cancel Badge
              </button>
            </div>
          ) : (
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/checkout/init", {
                    origin: window.location.origin, kind: "verified_badge",
                  });
                  window.location.href = data.url;
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}
              className="border border-secondary text-secondary px-10 py-3 rounded-sm uppercase tracking-[0.2em] text-xs hover:bg-secondary/10"
              data-testid="verified-checkout-btn"
            >
              Get Verified
            </button>
          )}
        </div>
      )}
    </Layout>
  );
}

export function SubscribeSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          if (cancelled) return;
          setStatus("paid");
          toast.success("Subscription activated!");
          setTimeout(() => nav("/welcome-practitioner"), 1500);
          return;
        }
        if (attempts >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch { if (!cancelled) setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <div className="parchment p-12 text-center" data-testid="subscribe-success">
        <div className="label-mystic mb-4">Stripe</div>
        {status === "checking" && <h1 className="font-serif text-4xl">Confirming your subscription...</h1>}
        {status === "paid" && <h1 className="font-serif text-5xl text-secondary">Welcome to the Coven.</h1>}
        {status === "timeout" && <h1 className="font-serif text-3xl">Still processing — refresh shortly.</h1>}
        {status === "error" && <h1 className="font-serif text-3xl text-destructive">Could not verify payment.</h1>}
      </div>
    </Layout>
  );
}
